import React, { useCallback, useEffect, useState } from 'react';
import type { InsuranceCardExtractionResponse } from '@features/patient-coverages/store/api';
import { needsSubscriberInfo } from '@features/patient-coverages/store/hooks';
import type { PatientCoverage } from '@features/patient-coverages/types';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Checkbox,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';

import { type InsuranceInfo, type PatientAddress } from '../../types/types';

import AddressCard from './AddressCard';
import InsuranceCardUpload from './InsuranceCardUpload';
import PayerSearch from './PayerSearch';

/**
 * Union type for both legacy and new coverage formats
 * Allows gradual migration from InsuranceInfo to PatientCoverage
 */
type CoverageData = InsuranceInfo | PatientCoverage;

interface InsuranceCardProps {
  insurance: CoverageData | null | undefined;
  onChange: (insurance: CoverageData | null) => void;
  onBlur?: () => void;
  title?: string;
  /** Derived lifecycle chip rendered beside the title (GDN-2411). */
  statusChip?: React.ReactNode;
  showDelete?: boolean;
  onDelete?: () => void;
  errors?: Record<string, string>;
  highlightedFields?: string[];
  disabled?: boolean;
  patientId?: string;
}

/**
 * Helper to check if coverage is new format (PatientCoverage)
 */
function isPatientCoverage(data: CoverageData): data is PatientCoverage {
  return 'patient_insurance_coverage_id' in data;
}

const InsuranceCard: React.FC<InsuranceCardProps> = ({
  insurance,
  onChange,
  onBlur,
  title = 'Insurance Information',
  statusChip,
  showDelete = false,
  onDelete,
  errors = {},
  highlightedFields = [],
  disabled = false,
  patientId,
}) => {
  const [showInsuredDetails, setShowInsuredDetails] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [subscriberFirstName, setSubscriberFirstName] = useState('');
  const [subscriberLastName, setSubscriberLastName] = useState('');

  // Get relationship field - handles both old and new formats
  const relationshipField = isPatientCoverage(insurance || {})
    ? (insurance as PatientCoverage).relationship_to_subscriber
    : (insurance as InsuranceInfo)?.relationship_to_insured;

  useEffect(() => {
    // Show insured/subscriber details if relationship is not 'self'
    if (relationshipField && relationshipField !== 'self') {
      setShowInsuredDetails(true);
    }

    // Initialize subscriber name fields from combined insured_name (legacy format)
    if (insurance && !isPatientCoverage(insurance)) {
      const insuredName = insurance.insured_name || '';
      const nameParts = insuredName.split(' ');
      setSubscriberFirstName(nameParts[0] || '');
      setSubscriberLastName(nameParts.slice(1).join(' ') || '');
    } else if (insurance && isPatientCoverage(insurance)) {
      setSubscriberFirstName(insurance.subscriber_first_name || '');
      setSubscriberLastName(insurance.subscriber_last_name || '');
    }
  }, [relationshipField, insurance]);

  const handleFieldChange = (field: string, value: string | null | boolean | PatientAddress) => {
    const updatedInsurance: CoverageData = {
      ...(insurance || {}),
      [field]: value === '' ? null : value,
    };
    onChange(updatedInsurance);
  };

  const handlePayerSelect = (payer: { id: string; name: string; eligibility: string } | null) => {
    const isNewFormat = insurance && isPatientCoverage(insurance);
    const consentKey = isNewFormat ? 'consent_on_file' : 'authorization_on_file';

    if (payer) {
      const updatedInsurance: CoverageData = {
        ...(insurance || {}),
        payer_id: payer.id,
        payer_name: payer.name,
        // Auto-check authorization when payer is selected
        [consentKey]: true,
      };
      onChange(updatedInsurance);
    } else {
      const updatedInsurance: CoverageData = {
        ...(insurance || {}),
        payer_id: null,
        payer_name: null,
      };
      onChange(updatedInsurance);
    }

    // Trigger immediate save for payer selection
    onBlur?.();
  };

  const handleRelationshipChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    const isNewFormat = insurance && isPatientCoverage(insurance);
    const relationshipKey = isNewFormat ? 'relationship_to_subscriber' : 'relationship_to_insured';

    handleFieldChange(relationshipKey, value);

    // Show/hide insured/subscriber details based on relationship
    if (value !== 'self' && value !== '') {
      setShowInsuredDetails(true);
    } else {
      setShowInsuredDetails(false);
      // Clear insured/subscriber fields if relationship is self
      if (value === 'self') {
        if (isNewFormat) {
          const updatedInsurance: PatientCoverage = {
            ...insurance,
            relationship_to_subscriber: 'self',
            subscriber_first_name: null,
            subscriber_last_name: null,
            subscriber_dob: null,
            subscriber_sex: null,
            subscriber_address: null,
          };
          onChange(updatedInsurance);
        } else {
          const updatedInsurance: InsuranceInfo = {
            ...(insurance as InsuranceInfo),
            relationship_to_insured: 'self',
            insured_name: null,
            insured_dob: null,
            insured_sex: null,
            insured_address: null,
          };
          onChange(updatedInsurance);
        }
        setSubscriberFirstName('');
        setSubscriberLastName('');
      }
      // Trigger immediate save when switching back to self/none — the
      // subscriber fields were cleared above so the payload is complete.
      onBlur?.();
    }
    // For non-self relationships, do NOT save immediately: the subscriber
    // fields are still blank and persisting them now would create a
    // coverage payers reject (GDN-2317). The save fires once the required
    // subscriber fields are filled (gated in PatientInsuranceEditor).
  };

  const handleSubscriberAddressChange = (address: PatientAddress | null) => {
    const isNewFormat = insurance && isPatientCoverage(insurance);
    const addressKey = isNewFormat ? 'subscriber_address' : 'insured_address';
    handleFieldChange(addressKey, address);
  };

  const handleSubscriberNameChange = (firstName: string, lastName: string) => {
    setSubscriberFirstName(firstName);
    setSubscriberLastName(lastName);

    if (insurance && isPatientCoverage(insurance)) {
      const updated: PatientCoverage = {
        ...insurance,
        subscriber_first_name: firstName || null,
        subscriber_last_name: lastName || null,
      };
      onChange(updated);
    } else {
      const updated: InsuranceInfo = {
        ...(insurance as InsuranceInfo),
        insured_name: `${firstName} ${lastName}`.trim() || null,
      };
      onChange(updated);
    }
  };

  // Get field values - handles both old and new formats
  const payer_id = insurance?.payer_id || null;
  const payer_name = insurance?.payer_name || null;
  const plan_name = insurance && 'plan_name' in insurance ? insurance.plan_name : null;
  const member_id = isPatientCoverage(insurance || {})
    ? (insurance as PatientCoverage)?.member_id
    : (insurance as InsuranceInfo)?.policy_number;
  const group_number = insurance && 'group_number' in insurance ? insurance.group_number : null;
  const claim_filing_code = insurance?.claim_filing_code ?? null;
  // Coverage date range — patient_coverage-only fields (no legacy equivalent).
  const effective_date =
    insurance && 'effective_date' in insurance ? insurance.effective_date : null;
  const termination_date =
    insurance && 'termination_date' in insurance ? insurance.termination_date : null;
  const consent_on_file = isPatientCoverage(insurance || {})
    ? (insurance as PatientCoverage)?.consent_on_file
    : (insurance as InsuranceInfo)?.authorization_on_file;
  const subscriber_dob = isPatientCoverage(insurance || {})
    ? (insurance as PatientCoverage)?.subscriber_dob
    : (insurance as InsuranceInfo)?.insured_dob;
  const subscriber_sex = isPatientCoverage(insurance || {})
    ? (insurance as PatientCoverage)?.subscriber_sex
    : (insurance as InsuranceInfo)?.insured_sex;
  const subscriber_address = isPatientCoverage(insurance || {})
    ? (insurance as PatientCoverage)?.subscriber_address
    : (insurance as InsuranceInfo)?.insured_address;

  const selectedPayer =
    payer_id && payer_name ? { id: payer_id, name: payer_name, eligibility: '' } : null;

  // Subscriber names are required whenever the patient is not the
  // subscriber. DOB remains collectable for claim readiness, but it must
  // not block saving coverage or eligibility checks: Nirvana's member_*
  // contract uses the patient's demographics, including for dependents.
  const requiresSubscriberDetails = needsSubscriberInfo({
    relationship_to_subscriber: relationshipField || undefined,
  });
  const subscriberRequiredError = (value: string | null | undefined): string | undefined =>
    requiresSubscriberDetails && !value?.trim()
      ? 'Required when relationship is not self'
      : undefined;

  const handleCardExtracted = useCallback(
    (data: InsuranceCardExtractionResponse) => {
      const isNewFormat = insurance && isPatientCoverage(insurance);
      const updated: CoverageData = {
        ...(insurance || {}),
        payer_name: data.payer_name ?? undefined,
        plan_name: data.plan_name ?? undefined,
        group_number: data.group_number ?? undefined,
        effective_date: data.effective_date ?? undefined,
        ...(isNewFormat
          ? {
              member_id: data.member_id ?? undefined,
              relationship_to_subscriber: data.subscriber_relationship ?? undefined,
              subscriber_first_name: data.subscriber_first_name ?? undefined,
              subscriber_last_name: data.subscriber_last_name ?? undefined,
            }
          : {
              policy_number: data.member_id ?? undefined,
              relationship_to_insured: data.subscriber_relationship ?? undefined,
              insured_name:
                data.subscriber_first_name || data.subscriber_last_name
                  ? `${data.subscriber_first_name || ''} ${data.subscriber_last_name || ''}`.trim()
                  : undefined,
            }),
      };
      onChange(updated);
      onBlur?.();
    },
    [insurance, onChange, onBlur],
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            {title}
          </Typography>
          {statusChip}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {patientId && (
            <InsuranceCardUpload
              patientId={patientId}
              onExtracted={handleCardExtracted}
              disabled={disabled}
            />
          )}
          {showDelete && onDelete && (
            <IconButton size="small" onClick={onDelete} color="error" disabled={disabled}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>

      <Grid container spacing={2}>
        {/* Payer Search */}
        <Grid size={{ xs: 12 }}>
          <PayerSearch
            onPayerSelect={handlePayerSelect}
            disabled={disabled}
            value={selectedPayer}
          />
          {/* Show Payer ID as simple label if present */}
          {insurance?.payer_id && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Payer ID: {insurance.payer_id}
            </Typography>
          )}
        </Grid>

        {/* Plan Name */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Plan Name (Optional)"
            value={plan_name || ''}
            onChange={e => handleFieldChange('plan_name', e.target.value)}
            onBlur={onBlur}
            variant="outlined"
            size="small"
            fullWidth
            disabled={disabled}
            error={!!errors.plan_name}
            helperText={errors.plan_name}
            sx={{
              ...(highlightedFields.includes('plan_name') && {
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  },
                },
              }),
            }}
          />
        </Grid>

        {/* Member/Policy Number */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Member/Policy ID Number"
            value={member_id || ''}
            onChange={e => {
              const fieldName = isPatientCoverage(insurance || {}) ? 'member_id' : 'policy_number';
              handleFieldChange(fieldName, e.target.value);
            }}
            onBlur={onBlur}
            variant="outlined"
            size="small"
            fullWidth
            disabled={disabled}
            error={!!errors.policy_number || !!errors.member_id}
            helperText={errors.policy_number || errors.member_id}
            sx={{
              ...((highlightedFields.includes('policy_number') ||
                highlightedFields.includes('member_id')) && {
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  },
                },
              }),
            }}
          />
        </Grid>

        {/* Group Number */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Group Number (Optional)"
            value={group_number || ''}
            onChange={e => handleFieldChange('group_number', e.target.value)}
            onBlur={onBlur}
            variant="outlined"
            size="small"
            fullWidth
            disabled={disabled}
            error={!!errors.group_number}
            helperText={errors.group_number}
            sx={{
              ...(highlightedFields.includes('group_number') && {
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  },
                },
              }),
            }}
          />
        </Grid>

        {/* Relationship to Subscriber/Insured */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth size="small" disabled={disabled}>
            <InputLabel>Relationship to Subscriber</InputLabel>
            <Select
              value={relationshipField || ''}
              onChange={handleRelationshipChange}
              onBlur={onBlur}
              label="Relationship to Subscriber"
              sx={{
                ...((highlightedFields.includes('relationship_to_insured') ||
                  highlightedFields.includes('relationship_to_subscriber')) && {
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: 2,
                    },
                  },
                }),
              }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              <MenuItem value="self">Self</MenuItem>
              <MenuItem value="spouse">Spouse</MenuItem>
              <MenuItem value="child">Child</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Consent/Authorization on File */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box
            sx={{
              borderRadius: 1,
              ...(highlightedFields.includes('consent_on_file') ||
              highlightedFields.includes('authorization_on_file')
                ? {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: 2,
                  }
                : {}),
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={consent_on_file || false}
                  onChange={e => {
                    const fieldName = isPatientCoverage(insurance || {})
                      ? 'consent_on_file'
                      : 'authorization_on_file';
                    handleFieldChange(fieldName, e.target.checked);
                    // Trigger immediate save for checkbox changes
                    onBlur?.();
                  }}
                  disabled={disabled}
                />
              }
              label="Consent/Authorization Signature on File"
            />
          </Box>
        </Grid>

        {/* Subscriber/Insured Person Details (if different from patient) */}
        {showInsuredDetails && (
          <>
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Subscriber Information
                </Typography>
              </Divider>
            </Grid>

            {/* Handle both formats: split first/last for new, combined for legacy */}
            {isPatientCoverage(insurance || {}) ? (
              <>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Subscriber First Name"
                    value={subscriberFirstName}
                    onChange={e => handleSubscriberNameChange(e.target.value, subscriberLastName)}
                    onBlur={onBlur}
                    variant="outlined"
                    size="small"
                    fullWidth
                    required={requiresSubscriberDetails}
                    disabled={disabled}
                    error={
                      !!errors.subscriber_first_name ||
                      !!subscriberRequiredError(subscriberFirstName)
                    }
                    helperText={
                      errors.subscriber_first_name || subscriberRequiredError(subscriberFirstName)
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Subscriber Last Name"
                    value={subscriberLastName}
                    onChange={e => handleSubscriberNameChange(subscriberFirstName, e.target.value)}
                    onBlur={onBlur}
                    variant="outlined"
                    size="small"
                    fullWidth
                    required={requiresSubscriberDetails}
                    disabled={disabled}
                    error={
                      !!errors.subscriber_last_name || !!subscriberRequiredError(subscriberLastName)
                    }
                    helperText={
                      errors.subscriber_last_name || subscriberRequiredError(subscriberLastName)
                    }
                  />
                </Grid>
              </>
            ) : (
              <Grid size={{ xs: 12, sm: 12 }}>
                <TextField
                  label="Subscriber Name"
                  value={`${subscriberFirstName} ${subscriberLastName}`.trim()}
                  onChange={e => {
                    const parts = e.target.value.split(' ');
                    handleSubscriberNameChange(parts[0] || '', parts.slice(1).join(' ') || '');
                  }}
                  onBlur={onBlur}
                  variant="outlined"
                  size="small"
                  fullWidth
                  required={requiresSubscriberDetails}
                  disabled={disabled}
                  error={
                    !!errors.insured_name ||
                    !!subscriberRequiredError(`${subscriberFirstName} ${subscriberLastName}`.trim())
                  }
                  helperText={
                    errors.insured_name ||
                    subscriberRequiredError(`${subscriberFirstName} ${subscriberLastName}`.trim())
                  }
                />
              </Grid>
            )}

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Subscriber Date of Birth"
                type="date"
                value={subscriber_dob || ''}
                onChange={e => {
                  const fieldName = isPatientCoverage(insurance || {})
                    ? 'subscriber_dob'
                    : 'insured_dob';
                  handleFieldChange(fieldName, e.target.value);
                }}
                onBlur={onBlur}
                variant="outlined"
                size="small"
                fullWidth
                disabled={disabled}
                error={!!errors.insured_dob || !!errors.subscriber_dob}
                helperText={
                  errors.insured_dob ||
                  errors.subscriber_dob ||
                  (requiresSubscriberDetails && !subscriber_dob
                    ? 'Required before claim submission; not required to save coverage or check eligibility.'
                    : undefined)
                }
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small" disabled={disabled}>
                <InputLabel>Subscriber Sex</InputLabel>
                <Select
                  value={subscriber_sex || ''}
                  onChange={e => {
                    const fieldName = isPatientCoverage(insurance || {})
                      ? 'subscriber_sex'
                      : 'insured_sex';
                    handleFieldChange(fieldName, e.target.value);
                    // Trigger immediate save for select changes
                    onBlur?.();
                  }}
                  label="Subscriber Sex"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Subscriber Address */}
            <Grid size={{ xs: 12 }}>
              <Box sx={{ mt: 1 }}>
                <AddressCard
                  address={subscriber_address || null}
                  onChange={handleSubscriberAddressChange}
                  onBlur={onBlur}
                  title="Subscriber Address"
                  disabled={disabled}
                  errors={errors}
                  highlightedFields={highlightedFields}
                />
              </Box>
            </Grid>
          </>
        )}

        {/* Coverage date range (GDN-2223, promoted to the card face in
            GDN-2411 — these dates now drive which plan bills for a date of
            service, so they must be visible, not buried in Advanced).
            Setting a termination date automatically retires this plan for
            later visit dates; a new plan's effective date takes over. */}
        <Grid size={{ xs: 12 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Effective Date"
              type="date"
              value={effective_date || ''}
              // Keyboard entry fires change per date segment; saving on
              // change persists partial years (e.g. "0020"). Debounced
              // autosave + save-on-blur instead.
              onChange={e => handleFieldChange('effective_date', e.target.value)}
              onBlur={onBlur}
              variant="outlined"
              size="small"
              disabled={disabled}
              error={!!errors.effective_date}
              helperText={errors.effective_date}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ flex: 1, minWidth: 160 }}
            />
            <TextField
              label="Termination Date"
              type="date"
              value={termination_date || ''}
              onChange={e => handleFieldChange('termination_date', e.target.value)}
              onBlur={onBlur}
              variant="outlined"
              size="small"
              disabled={disabled}
              error={!!errors.termination_date}
              helperText={
                errors.termination_date ||
                (termination_date ? 'Visits after this date bill the next plan on file' : undefined)
              }
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ flex: 1, minWidth: 160 }}
            />
          </Box>
        </Grid>

        {/* Advanced — collapsed drawer by default (GDN-2025).
            Durable per-coverage filing-code override; materializes onto claims. */}
        <Grid size={{ xs: 12 }}>
          <Divider sx={{ mt: 1 }} />
          <Box
            role="button"
            tabIndex={0}
            aria-expanded={showAdvanced}
            onClick={() => setShowAdvanced(prev => !prev)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setShowAdvanced(prev => !prev);
              }
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              cursor: 'pointer',
              userSelect: 'none',
              py: 1,
              color: 'text.secondary',
            }}
          >
            {showAdvanced ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Advanced
            </Typography>
          </Box>
          <Collapse in={showAdvanced} unmountOnExit>
            <Box sx={{ pb: 1, maxWidth: 380 }}>
              <FormControl fullWidth size="small" disabled={disabled}>
                <InputLabel>Claim filing indicator</InputLabel>
                <Select
                  value={claim_filing_code || ''}
                  onChange={e => {
                    handleFieldChange('claim_filing_code', e.target.value || null);
                    onBlur?.();
                  }}
                  label="Claim filing indicator"
                >
                  <MenuItem value="">
                    <em>Auto (derive from payer)</em>
                  </MenuItem>
                  <MenuItem value="FI">FI — Federal Employee Program (BCBS FEP)</MenuItem>
                  <MenuItem value="BL">BL — Blue Cross / Blue Shield</MenuItem>
                  <MenuItem value="HM">HM — HMO</MenuItem>
                  <MenuItem value="MB">MB — Medicare Part B</MenuItem>
                  <MenuItem value="CI">CI — Commercial Insurance</MenuItem>
                  <MenuItem value="MC">MC — Medicaid</MenuItem>
                  <MenuItem value="CH">CH — CHAMPUS / TRICARE</MenuItem>
                  <MenuItem value="WC">WC — Workers&apos; Compensation</MenuItem>
                </Select>
              </FormControl>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: 'block' }}
              >
                Usually Auto. Set e.g. FI for a Federal Employee Program plan.
              </Typography>
            </Box>
          </Collapse>
        </Grid>
      </Grid>
    </Box>
  );
};

export default InsuranceCard;
